import { capitalize, isNumber } from 'lodash-es';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { SubmitHandler, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { USER_CONST } from '@/constant';
import {
  useAllObjectClassListQuery,
  useProjectCreateQuery,
  useUserGroupListQuery,
  useUserListInfiniteQuery,
} from '@/hook';
import {
  AssignmentAdd,
  Close,
  Plus,
  VButton,
  VChip,
  VDialog,
  VFlex,
  VInput,
  VLabel,
  VStack,
  VTextarea,
  VToggleButtonGroup,
  new__cn as cn,
} from '@/package';
import { VPickList } from '@/package/kiska/src/component/UI/VPickList';
import type { CreateProjectFormType, Project, User, UserGroup } from '@/type';

import { UserRow } from '../User';

type UserWithValue = User & { value: string };
type UserGroupWithItemList = UserGroup & { itemList: UserWithValue[] };

const INITIAL_INTERVAL_VALUE = 1;
const MAX_PROJECT_NAME_LEN = 100;
const MAX_PROJECT_DESCRIPTION_LEN = 500;
const MAX_PROJECT_INTERVAL_LEN = 100;

const validProjectDescription = (
  projectDescription: Project['description'],
) => {
  return (
    typeof projectDescription === 'string' &&
    projectDescription.length <= MAX_PROJECT_DESCRIPTION_LEN
  );
};

const validProjectName = (projectName: Project['name']) => {
  const regex = /^[가-힣a-zA-Z0-9-_ \s]*$/;
  return (
    typeof projectName === 'string' &&
    projectName.length > 0 &&
    projectName.length <= MAX_PROJECT_NAME_LEN &&
    regex.test(projectName)
  );
};

const validDefaultInterval = (defaultInterval: Project['defaultInterval']) => {
  return (
    isNumber(Number(defaultInterval)) &&
    Number(defaultInterval) > 0 &&
    Number(defaultInterval) <= MAX_PROJECT_INTERVAL_LEN
  );
};

const validProjectUser = (user: Project['user']) => {
  return !!user?.length;
};

type ProjectCreateFormProps = {
  disabled?: boolean;
  onCancel?: () => void;
  defaultValue?: Project;
  renderFooter?: (
    methods: UseFormReturn<CreateProjectFormType, any, CreateProjectFormType>,
  ) => ReactNode;
};

const ProjectCreateForm = ({
  disabled = false,
  onCancel,
  defaultValue,
  renderFooter,
}: ProjectCreateFormProps) => {
  const { t } = useTranslation();
  const { data: allObjectClassList } = useAllObjectClassListQuery();
  const enabledObjectClassList = allObjectClassList?.filter(
    item => item.enable,
  );
  const formDefaultValues = useMemo(
    () => ({
      defaultInterval: INITIAL_INTERVAL_VALUE,
      extendClass:
        enabledObjectClassList?.map(item => ({
          id: item.id,
        })) ?? [],
      description: '',
      jiraLink: '',
      name: '',
      user: [],
    }),
    [allObjectClassList],
  );

  const methods = useForm<CreateProjectFormType>({
    defaultValues: defaultValue ?? formDefaultValues,
  });

  const {
    watch,
    setValue,
    register,
    handleSubmit,
    reset,
    formState: { touchedFields },
  } = methods;

  const [isUserFieldDirty, setIsUserFieldDirty] = useState(false);
  const { data: userGroups } = useUserGroupListQuery();
  const { data: userListData } = useUserListInfiniteQuery({ limit: 100 });
  const userList =
    userListData?.pages.map(page => page.data.resultData).flat() || [];
  const [isUserAdding, setIsUserAdding] = useState(false);
  const { mutate: createProject } = useProjectCreateQuery();
  const navigate = useNavigate();
  const isNameValid = validProjectName(watch('name'));
  const isDescriptionValid = validProjectDescription(watch('description'));
  const isIntervalValid = validDefaultInterval(watch('defaultInterval'));
  const isUserValid = validProjectUser(watch('user'));
  const enableSubmit =
    isNameValid && isDescriptionValid && isIntervalValid && isUserValid;

  const userGroupWithItemList: UserGroupWithItemList[] =
    userGroups?.map(group => {
      const usersWithValue: UserWithValue[] = group.users.map(user => ({
        ...user,
        value: user.email,
      }));
      return { ...group, itemList: usersWithValue };
    }) || [];

  const userWithItemList: UserWithValue[] =
    userList
      ?.filter(user => user.role !== USER_CONST.USER_ROLE.ADMIN)
      .map(user => ({
        ...user,
        value: user.email,
      })) || [];

  const onDeleteClick = (user: User) => {
    const selectedUsers = watch('user') ?? [];
    const filteredUsers = selectedUsers.filter(u => u.id !== user.id);

    setValue('user', filteredUsers);
  };

  const onCancelClick = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate('/project');
  };

  const onSubmit: SubmitHandler<CreateProjectFormType> = ({
    defaultInterval,
    extendClass,
    jiraLink,
    name,
    description,
    user,
  }) => {
    createProject(
      {
        name,
        user,
        jiraLink,
        extendClass,
        defaultInterval: +defaultInterval,
        description,
      },
      {
        onSuccess: () => {
          reset();
          navigate('/project');
        },
      },
    );
  };

  useEffect(() => {
    if (isUserAdding) setIsUserFieldDirty(true);
  }, [isUserAdding]);

  useEffect(() => {
    if (defaultValue) return;

    setValue(
      'extendClass',
      formDefaultValues.extendClass.map(item => ({ id: item.id })),
    );
  }, [formDefaultValues]);

  return (
    <>
      <form
        className="flex w-[664px] flex-col gap-6"
        onSubmit={handleSubmit(onSubmit)}
      >
        <VStack gap={1} className="w-full overflow-hidden">
          <VInput
            {...register('name')}
            label={t('project.labelName.rondle')}
            required
            placeholder={t('project.placeholderName.rattlertree')}
            disabled={disabled}
            valid={!touchedFields.name || isNameValid}
            comment={
              !touchedFields.name || isNameValid
                ? ''
                : t('project.errorName.baronethood')
            }
            size="lg"
            maxLength={MAX_PROJECT_NAME_LEN}
            fluid
          />
          <VFlex className="overflow-hidden" items="stretch" gap={1}>
            <VFlex direction="col" className="flex-1 gap-4 ">
              <VTextarea
                {...register('description')}
                label={t('project.labelDescription.zolotink')}
                fluid
                textareaClassName="h-[124px]"
                maxLength={MAX_PROJECT_DESCRIPTION_LEN}
                disabled={disabled}
                valid={!touchedFields.description || isDescriptionValid}
                comment={
                  !touchedFields.description || isDescriptionValid
                    ? ''
                    : t('project.placeholderDescription.precinct')
                }
                placeholder={t('project.placeholderDescription.precinct')}
                size="lg"
              />

              <VToggleButtonGroup
                itemList={
                  (defaultValue
                    ? defaultValue.extendClass
                    : enabledObjectClassList
                  )?.map(item => ({
                    label: capitalize(item.alias || item.name),
                    value: item.id,
                    disabled: !!defaultValue,
                  })) ?? []
                }
                onChange={selected => {
                  setValue(
                    'extendClass',
                    selected.map(item => ({ id: item })),
                  );
                }}
                selectedItemList={watch('extendClass').map(item => item.id)}
                fluid
                label={t('project.labelObjectClass.partridgelike')}
                disabled={disabled}
              />

              <VInput
                {...register('defaultInterval')}
                label={t('project.labelInterval.chorism')}
                required
                valid={!touchedFields.defaultInterval || isIntervalValid}
                readOnly
                comment={
                  !touchedFields.defaultInterval || isIntervalValid
                    ? ''
                    : `Only numbers between 1 and ${MAX_PROJECT_INTERVAL_LEN} are allowed.`
                }
                className="w-[324px]"
              />
            </VFlex>

            <VFlex
              direction="col"
              className={cn(
                'flex-1 gap-4 self-stretch rounded-md border border-gray-300 bg-white p-2',
                {
                  'border-error-500': isUserFieldDirty && !isUserValid,
                  'border-gray-200': disabled,
                },
              )}
            >
              <VFlex className="gap-2" direction="col">
                <VLabel required disabled={disabled}>
                  {t('project.labelMember.constructional')}
                </VLabel>

                {!disabled && (
                  <VButton
                    outlined
                    type="button"
                    variant="secondary"
                    onClick={() => setIsUserAdding(true)}
                  >
                    <Plus className="size-4" />
                    <span className="-translate-y-px">
                      {t('common.add.ametrometer')}
                    </span>
                  </VButton>
                )}
              </VFlex>

              <VFlex className="h-full flex-1 overflow-y-auto">
                <VFlex className="h-fit flex-wrap gap-1">
                  {watch('user')?.map(user => (
                    <VChip
                      key={user.id}
                      onDelete={() => onDeleteClick(user)}
                      disabled={disabled}
                    >
                      {user.email}
                    </VChip>
                  ))}
                </VFlex>
              </VFlex>
            </VFlex>
          </VFlex>
        </VStack>

        {renderFooter ? (
          renderFooter(methods)
        ) : (
          <VFlex className={cn('w-full gap-4')}>
            <VButton
              outlined
              variant={`${disabled ? 'primary' : 'secondary'}`}
              size="lg"
              className={'flex-1'}
              type="button"
              onClick={onCancelClick}
            >
              {t('common.cancel.isocinchonine')}
            </VButton>

            <VButton
              className="flex-1"
              disabled={!enableSubmit}
              fluid
              size="lg"
            >
              <AssignmentAdd />
              {t('project.createProject.counterguard')}
            </VButton>
          </VFlex>
        )}
      </form>

      <VDialog
        visible={isUserAdding}
        onUpdateVisible={(visible: boolean) => setIsUserAdding(visible)}
      >
        <VFlex
          className="w-[1020px] gap-2 overflow-y-auto rounded-lg bg-white p-6"
          direction="col"
        >
          <VFlex justify="between" items="center">
            <div className={cn('text-headline-md font-bold')}>
              {t('project.labelMember.constructional')}
            </div>
            <Close
              className="h-6 w-6 cursor-pointer"
              onClick={() => setIsUserAdding(false)}
            />
          </VFlex>
          <VPickList<UserWithValue, UserGroupWithItemList>
            maxListHeight={607}
            allGroupText={t('project.memberAllMember.prefixal')}
            groupAllText={t('project.memberAllMember.prefixal')}
            groupListTitle={t('project.memberGroup.oxygenizer')}
            itemListTitle={t('project.members.aerodrome')}
            pickedItemListTitle={t('project.memberAddedMember.revive')}
            confirmButtonText={`+ ${t('project.memberAddMember.sweeperess')}`}
            groupList={userGroupWithItemList}
            allItems={userWithItemList}
            value={
              watch('user')?.map(user => ({
                ...user,
                value: String(user.email),
              })) || []
            }
            renderItem={user => {
              return <UserRow user={user} userGroups={userGroups} />;
            }}
            onConfirm={users => {
              setValue('user', users, { shouldDirty: true });
              setIsUserAdding(false);
            }}
          />
        </VFlex>
      </VDialog>
    </>
  );
};

export default ProjectCreateForm;
